import { t as uiText } from '../i18n';
// src/components/PreviewMetaBadges.tsx
import React from 'react';
import { PreviewMeta } from '../types/preview';
import './PreviewMetaBadges.css';
import pictureIcon from '../assets/image/icon/picture.png';
import filesIcon from '../assets/image/icon/files.png';
import internalLinkIcon from '../assets/image/icon/internalLink_tr.png';
import externalLinkIcon from '../assets/image/icon/externalLink.png';

interface PreviewMetaBadgesProps {
  previewMeta?: PreviewMeta;
  isMobile?: boolean;
  showImages?: boolean;
  showAttachments?: boolean;
  showLinks?: boolean;
  showInternalLinks?: boolean;
  showExternalLinks?: boolean;
  combineLinkTypes?: boolean;  // リンク種別を統合表示するか
}

const PreviewMetaBadges: React.FC<PreviewMetaBadgesProps> = ({
  previewMeta,
  isMobile = false,
  showImages = true,
  showAttachments = true,
  showLinks = true,
  showInternalLinks = true,
  showExternalLinks = true,
  combineLinkTypes = false
}) => {
  if (!previewMeta) return null;

  const badges = [];

  // 画像バッジ
  if (showImages && previewMeta.hasImages && previewMeta.imageCount > 0) {
    badges.push(
      <span 
        key="images" 
        className="preview-badge image-badge"
        title={uiText("{{imageCount}} images", { imageCount: previewMeta.imageCount })}
      >
        <img 
          src={pictureIcon} 
          alt={uiText("Image")} 
          style={{ width: '12px', height: '12px', marginRight: '4px', filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(747%) hue-rotate(15deg) brightness(91%) contrast(87%)' }}
        />
        {previewMeta.imageCount}
      </span>
    );
  }

  // 添付ファイルバッジ
  if (showAttachments && previewMeta.hasAttachments && previewMeta.attachmentCount > 0) {
    const typeText = previewMeta.attachmentTypes.length > 0 
      ? ` (${previewMeta.attachmentTypes.slice(0, 2).join(', ')}${previewMeta.attachmentTypes.length > 2 ? '...' : ''})`
      : '';
    
    badges.push(
      <span 
        key="attachments" 
        className="preview-badge attachment-badge"
        title={uiText("{{attachmentCount}} attachments{{typeText}}", { attachmentCount: previewMeta.attachmentCount, typeText: typeText })}
      >
        <img 
          src={filesIcon} 
          alt={uiText("Attachments")} 
          style={{ width: '12px', height: '12px', marginRight: '4px', filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(747%) hue-rotate(15deg) brightness(91%) contrast(87%)' }}
        />
        {previewMeta.attachmentCount}
      </span>
    );
  }

  // リンクバッジ（統合表示 vs 分離表示）
  if (combineLinkTypes) {
    // 統合表示：従来通り
    if (showLinks && previewMeta.hasLinks) {
      badges.push(
        <span 
          key="links" 
          className="preview-badge link-badge"
          title={uiText("Has link")}
        >
          🔗
        </span>
      );
    }
  } else {
    // 分離表示：内部リンクと外部リンクを分けて表示
    if (showInternalLinks && previewMeta.hasInternalLinks && previewMeta.internalLinkCount! > 0) {
      badges.push(
        <span 
          key="internal-links" 
          className="preview-badge internal-link-badge"
          title={uiText("{{internalLinkCount}} internal links", { internalLinkCount: previewMeta.internalLinkCount })}
        >
          <img 
            src={internalLinkIcon} 
            alt={uiText("Internal link")} 
            style={{ width: '12px', height: '12px', marginRight: '4px', filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(747%) hue-rotate(15deg) brightness(91%) contrast(87%)' }}
          />
          {previewMeta.internalLinkCount}
        </span>
      );
    }
    
    if (showExternalLinks && previewMeta.hasExternalLinks && previewMeta.externalLinkCount! > 0) {
      badges.push(
        <span 
          key="external-links" 
          className="preview-badge external-link-badge"
          title={uiText("{{externalLinkCount}} external links", { externalLinkCount: previewMeta.externalLinkCount })}
        >
          <img 
            src={externalLinkIcon} 
            alt={uiText("External link")} 
            style={{ width: '12px', height: '12px', marginRight: '4px', filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(747%) hue-rotate(15deg) brightness(91%) contrast(87%)' }}
          />
          {previewMeta.externalLinkCount}
        </span>
      );
    }
  }

  if (badges.length === 0) return null;

  return (
    <div 
      className="preview-meta-badges"
      style={{
        display: 'flex',
        gap: isMobile ? '4px' : '6px',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginTop: '4px'
      }}
    >
      {badges}
    </div>
  );
};

export default PreviewMetaBadges;
